import { captureException } from '@sentry/nextjs';
import type { AnalyticsID } from 'lib/analytics/constants';
import Script from 'next/script';
import type { ReactElement } from 'react';

import type { ScriptID } from './constants';

type ScriptStrategy = 'beforeInteractive' | 'afterInteractive' | 'lazyOnload';
export type ScriptCondition =
  'cookiesAccepted' | 'isUS' | 'isVibeGalleryOnPage';

type Conditions = {
  OR?: ScriptCondition[];
  AND?: ScriptCondition[];
};

interface ScriptConfig {
  id: AnalyticsID | ScriptID;
  strategy: ScriptStrategy;
  src?: string;
  content?: string;
  conditions?: Conditions;
  priority?: boolean;
  attributes?: Record<string, string | number | undefined>;
  loadCallback?: () => void;
  isLoaded?: () => boolean;
}

const reportScriptError = (scriptId: string) => {
  captureException(new Error(`Script failed to load: ${scriptId}`), {
    level: 'warning',
    tags: { scriptId },
  });
};

export class ScriptManager {
  private priorityScripts: Set<string> = new Set();

  private loadedScripts: Set<string> = new Set();

  private scriptConfigs: Map<string, ScriptConfig> = new Map();

  private conditions: Map<ScriptCondition, boolean> = new Map();

  private subscribers: Set<() => void> = new Set();

  constructor() {
    this.conditions.set('cookiesAccepted', false);
    this.conditions.set('isUS', false);
    this.conditions.set('isVibeGalleryOnPage', false);
  }

  subscribe(callback: () => void): () => void {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  private notifySubscribers(): void {
    this.subscribers.forEach((callback) => callback());
  }

  setCondition(name: ScriptCondition, value: boolean): void {
    const currentValue = this.conditions.get(name);
    if (currentValue !== value) {
      this.conditions.set(name, value);
      this.notifySubscribers();
    }
  }

  registerScript(config: ScriptConfig): void {
    this.scriptConfigs.set(config.id, config);

    if (config.priority) {
      this.priorityScripts.add(config.id);
    }
  }

  handleScriptLoad(scriptId: string): void {
    if (this.loadedScripts.has(scriptId)) return;

    this.loadedScripts.add(scriptId);
    const config = this.scriptConfigs.get(scriptId);
    try {
      config?.loadCallback?.();
    } finally {
      this.notifySubscribers();
    }
  }

  clearScripts(): void {
    this.loadedScripts.forEach((id) => {
      if (this.priorityScripts.has(id)) return;

      const script = document.getElementById(id);
      if (script) {
        script.remove();
      }

      this.scriptConfigs.delete(id);
      this.loadedScripts.delete(id);
    });
  }

  isScriptLoaded(id: string): boolean {
    return this.loadedScripts.has(id);
  }

  getCondition(name: ScriptCondition): boolean {
    return this.conditions.get(name) || false;
  }

  private shouldLoadScript({ conditions }: ScriptConfig): boolean {
    if (!conditions) {
      return true;
    }

    const results: boolean[] = [];

    if (conditions.OR) {
      const orResult = conditions.OR.some((conditionName) =>
        this.conditions.get(conditionName),
      );
      results.push(orResult);
    }

    if (conditions.AND) {
      const andResult = conditions.AND.every((conditionName) =>
        this.conditions.get(conditionName),
      );
      results.push(andResult);
    }

    return results.every((result) => result);
  }

  getScriptElements(): ReactElement[] {
    const scripts = Array.from(this.scriptConfigs.values())
      .filter((config) => this.shouldLoadScript(config))
      .map((config) => {
        const scriptProps = {
          id: config.id,
          strategy: config.strategy,
          onLoad: () => this.handleScriptLoad(config.id),
          onError: () => reportScriptError(config.id),
          ...(config?.attributes ? config.attributes : {}),
        };

        if (config.src) {
          return <Script key={config.id} {...scriptProps} src={config.src} />;
        }

        return (
          <Script
            key={config.id}
            {...scriptProps}
            dangerouslySetInnerHTML={{ __html: config.content || '' }}
            /* INFO: onLoad should do the job, but onLoad does not fire if
             * the script is loaded with dangerouslySetInnerHTML, so we use
             * onReady instead. */
            onReady={() => this.handleScriptLoad(config.id)}
          />
        );
      });

    return scripts;
  }
}
